import { useAuthStore } from '@/stores/authStore';
import { AUTH_REDIRECT } from '@/lib/auth-prefs';
import { removePref, UI_PREFS, writePref } from '@/lib/ui-prefs';

/** Prevents multiple concurrent 401s from stacking redirects. */
let redirectingToLogin = false;

/**
 * Only allow same-origin relative paths (open-redirect safe).
 */
export function sanitizeReturnPath(path: string | null | undefined): string {
  if (!path) return '/';
  const trimmed = path.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return '/';
  if (trimmed.startsWith('/login')) return '/';
  return trimmed;
}

export function getCurrentReturnPath(): string {
  if (typeof window === 'undefined') return '/';
  return sanitizeReturnPath(
    `${window.location.pathname}${window.location.search}${window.location.hash}`
  );
}

/** Path used by `<Navigate to=…>` / links when auth is required. */
export function buildLoginPath(returnTo?: string): string {
  const returnPath = sanitizeReturnPath(returnTo ?? getCurrentReturnPath());
  if (returnPath === '/') return '/login';
  return `/login?redirect=${encodeURIComponent(returnPath)}`;
}

function clearLocalAuthState() {
  useAuthStore.setState({
    user: null,
    loading: false,
    error: null,
    sessionUnreachable: false,
    sessionRefreshFailed: false,
  });
  if (typeof window === 'undefined') return;
  removePref(UI_PREFS.authLegacyToken);
  removePref(UI_PREFS.authLegacyUser);
}

/**
 * Clear the local session and send the user to login, preserving the page they were on.
 * Safe to call from outside React (e.g. fetchApi).
 */
/** Pages anyone may read without an account (Meta and Google link to them). */
const PUBLIC_POLICY_PATHS = ['/privacy', '/terms', '/data-deletion'];

export function redirectToLoginForExpiredSession(returnTo?: string): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/login')) return;
  // Policy pages are public: an expired session there is cleared, not sent to login.
  if (PUBLIC_POLICY_PATHS.includes(window.location.pathname)) {
    clearLocalAuthState();
    return;
  }
  if (redirectingToLogin) return;
  redirectingToLogin = true;

  const returnPath = sanitizeReturnPath(returnTo ?? getCurrentReturnPath());
  if (returnPath !== '/') {
    writePref(AUTH_REDIRECT, returnPath);
  }

  clearLocalAuthState();
  window.location.assign(buildLoginPath(returnPath));
}

/**
 * Whether redirectToLoginForExpiredSession would leave this page: not on /login or a policy page,
 * and no redirect already under way.
 */
export function willRedirectToLogin(): boolean {
  if (typeof window === 'undefined') return false;
  const { pathname } = window.location;
  return !pathname.startsWith('/login') && !PUBLIC_POLICY_PATHS.includes(pathname) && !redirectingToLogin;
}
